import { BASE_GROUND_OFFSET, DECORATION_NOISE_SCALE } from "../config";
import { BLOCK, type BiomeMap, type BlockKind, type VoxelWorld } from "../types";
import { BIOME_RULES, type DecorationRule } from "./biomeRules";
import { makeNoise2D } from "./heightmap";

/**
 * Place decorations per cell according to its biome rules. Decorations placed
 * after trees so tree footprints are respected.
 *
 * Each decoration kind gets its own seeded noise (offset by index) so renders
 * are deterministic and decoration types don't collide systemically.
 */
export function placeDecorations(
    world: VoxelWorld,
    heights: number[][],
    biomes: BiomeMap,
    seed: number,
): void {
    const N = world.size;

    const noiseByType = new Map<BlockKind, ReturnType<typeof makeNoise2D>>();
    const getNoise = (type: BlockKind, idx: number) => {
        let n = noiseByType.get(type);
        if (!n) {
            n = makeNoise2D(seed * 13.37 + 1000 + idx * 17);
            noiseByType.set(type, n);
        }
        return n;
    };

    let typeCounter = 0;
    const typeIndex = new Map<BlockKind, number>();
    const indexOf = (type: BlockKind) => {
        let i = typeIndex.get(type);
        if (i === undefined) {
            i = typeCounter++;
            typeIndex.set(type, i);
        }
        return i;
    };

    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            const rules: readonly DecorationRule[] | undefined =
                BIOME_RULES[biomes.biome[x][z]].decorations;
            if (!rules || rules.length === 0) continue;

            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            const surface = world.get(x, topAbs, z);
            const above = world.get(x, topAbs + 1, z);

            for (const rule of rules) {
                if (rule.placeAbove === BLOCK.Water) {
                    // Floating decoration (e.g. lily_pad) sits on top of a water cell.
                    if (above !== BLOCK.Water) continue;
                    const n = (getNoise(rule.type, indexOf(rule.type))(
                        x * DECORATION_NOISE_SCALE,
                        z * DECORATION_NOISE_SCALE,
                    ) + 1) / 2;
                    if (n > rule.density) continue;
                    if (world.get(x, topAbs + 2, z)) continue;
                    world.set(x, topAbs + 2, z, rule.type);
                    break;
                }

                if (!surface || !rule.placeOn.includes(surface)) continue;
                if (above) continue; // leave space; trees and snow_block already win

                const n = (getNoise(rule.type, indexOf(rule.type))(
                    x * DECORATION_NOISE_SCALE,
                    z * DECORATION_NOISE_SCALE,
                ) + 1) / 2;
                if (n > rule.density) continue;

                const h = rule.height ?? 1;
                for (let i = 1; i <= h; i++) {
                    world.set(x, topAbs + i, z, rule.type);
                }
                break; // one decoration per cell
            }
        }
    }
}
